import { levenshteinRatio } from './similarity'
import { normalizeForComparison, normalizeNameForComparison, normalizeSuite } from './normalize'
import type { Location } from '../types'

/**
 * @see location-matching-algorithm.md#configuration-parameters
 */
export interface MatchingConfig {
  PROXIMITY_RADIUS_METERS: number
  MATCH_THRESHOLD: number
  NAME_SIMILARITY_WEIGHT: number
  ADDRESS_SIMILARITY_WEIGHT: number
  DISTANCE_WEIGHT: number
  SUITE_MISMATCH_PENALTY: number
}

export const DEFAULT_MATCHING_CONFIG: MatchingConfig = {
  PROXIMITY_RADIUS_METERS: 50,
  MATCH_THRESHOLD: 0.7,
  NAME_SIMILARITY_WEIGHT: 0.4,
  ADDRESS_SIMILARITY_WEIGHT: 0.3,
  DISTANCE_WEIGHT: 0.3,
  SUITE_MISMATCH_PENALTY: 0.5,
}

export interface ScoredCandidate {
  location: Location
  score: number
  distanceMeters: number
}

export interface CandidateInput {
  name?: string
  streetAddress: string
  addressLineTwo?: string
}

/**
 * Score a candidate location against input data.
 *
 * @see location-matching-algorithm.md#scoring-algorithm
 */
export function scoreCandidate(
  candidate: Location & { distance_meters: number },
  input: CandidateInput,
  config: MatchingConfig = DEFAULT_MATCHING_CONFIG
): ScoredCandidate {
  const inputNameNorm = input.name ? normalizeNameForComparison(input.name) : ''
  const candidateNameNorm = normalizeNameForComparison(candidate.name || '')

  const inputAddressNorm = normalizeForComparison(input.streetAddress)
  const candidateAddressNorm = normalizeForComparison(candidate.address_line_one || candidate.address || '')

  const nameSimilarity = inputNameNorm && candidateNameNorm
    ? levenshteinRatio(inputNameNorm, candidateNameNorm)
    : 0.5

  const addressSimilarity = levenshteinRatio(inputAddressNorm, candidateAddressNorm)

  const distanceScore = 1 - (candidate.distance_meters / config.PROXIMITY_RADIUS_METERS)

  let suitePenalty = 0
  const inputSuite = normalizeSuite(input.addressLineTwo)
  const candidateSuite = normalizeSuite(candidate.address_line_two)

  if (inputSuite && candidateSuite && inputSuite !== candidateSuite) {
    suitePenalty = config.SUITE_MISMATCH_PENALTY
  }

  const score = (
    (nameSimilarity * config.NAME_SIMILARITY_WEIGHT) +
    (addressSimilarity * config.ADDRESS_SIMILARITY_WEIGHT) +
    (distanceScore * config.DISTANCE_WEIGHT) -
    suitePenalty
  )

  return {
    location: candidate,
    score: Math.max(0, Math.min(1, score)),
    distanceMeters: candidate.distance_meters,
  }
}

/**
 * Score and sort multiple candidates.
 */
export function scoreCandidates(
  candidates: Array<Location & { distance_meters: number }>,
  input: CandidateInput,
  config: MatchingConfig = DEFAULT_MATCHING_CONFIG
): ScoredCandidate[] {
  return candidates
    .map(candidate => scoreCandidate(candidate, input, config))
    .sort((a, b) => b.score - a.score)
}
